import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import KaartMetLegenda from './KaartMetLegenda';
import { fmtId } from '../util/format';
import { lookupPerceel, getKeurkaartCheck } from '../api/api';
import { gebiedStijl } from '../util/gebiedstijl';
import {
  SOORT_NAAM, STATUS_TEKST, boorstatusVan, KWETSBARE_TYPES, nieuweSleutel, isMeldbaar,
  kwetsbaarOpPunt, gebiedenOpPunt, maakKwetsbaarToets,
  exacteTreffer, zoekKandidaten, ToevoegMenu,
} from './PuttenPaneel';

/* PuttenKaart — de putten van een melding samenstellen op de kaart.

   ── Waarom niet de tabel ──────────────────────────────────────────────────

   Een tabel is goed in lézen: alle putten onder elkaar, kolom voor kolom te
   vergelijken. Maar putten aanwíjzen doe je niet in een tabel; dat doe je op
   de kaart. Het meldingsformulier lost dat al op en doet dat goed: kaart
   links, een kolom rechts die van lijst naar paneel wisselt. Wie een put
   plaatst blijft met zijn oog op de kaart, en het paneel verschijnt naast de
   plek waar hij klikte in plaats van eroverheen.

   Die indeling is hier overgenomen, met twee verschillen die uit de rol van
   het waterschap volgen:

     · Geen gebiedswaarschuwingen. De melder moet weten dat hij in een
       waterstaatswerk zit; de behandelaar weet dat en registreert gewoon.
     · Een kwetsbaar gebied wordt geweigerd, niet uitgelegd. Daar kan geen put
       via een melding worden geregistreerd, dus is er niets te kiezen.

   ── De kolom heeft twee gedaanten ─────────────────────────────────────────

   lijst    alle putten van deze inrichting als kaartjes, met de teller erboven
   paneel   één ding tegelijk: een put, een plek waar je een put wil zetten,
            een registerput die je kunt koppelen, of een weigering

   Je gaat van lijst naar paneel door te klikken (op de kaart of op een
   kaartje) en terug met de Lijst-knop. Nooit allebei tegelijk: dan zou de
   kolom twee keer zo lang worden als de kaart en verspringt alles onder je
   handen.

   ── Twee velden erbij ────────────────────────────────────────────────────

   besluitnummer       het besluitnummer van de omgevingsvergunning voor de
                       waterstaatswerken waarin de put ligt. Eén per put, ook
                       als hij in meer werken ligt.

   In welke werken een put ligt staat níet in het puttenmodel: dat rekenen we
   hier uit tegen de gebiedsgeometrieën die de kaart toch al geladen heeft.
   Eén bron voor alle vier de manieren waarop een put in de lijst komt, in
   plaats van een veld dat bij de ene weg wel en bij de andere niet gevuld is.

   ── Bevestiging ───────────────────────────────────────────────────────────

   Een toegevoegde put verscheen eerder onderin een lijst waar je op dat
   moment niet naar keek. Nu gebeuren er drie dingen tegelijk: het paneel gaat
   over in het detail van de nieuwe put met een groene regel erboven, de
   marker op de kaart wordt groter en donker omrand, en het kaartje in de
   lijst licht kort op. */

const meters = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (!Number.isFinite(n)) return null;
  return String(n).replace('.', ',');
};

const SOORT_KLEUR = { gekoppeld: '#0071e3', ongeregistreerd: '#0071e3', nieuw: '#ff9500' };
/* De kleur volgt de boorstatus, niet de herkomst: een registerput die de
   melder als nog te boren aanwijst is oranje, net als een geprikte. */
const stipSoort = (p) => (p.verwijderd ? 'afgedicht' : boorstatusVan(p) === 'te_boren' ? 'nieuw' : p.soort);

/* Afstand op de pil: één decimaal. Twee cijfers achter de komma suggereren een
   nauwkeurigheid die een muisklik niet heeft. */
const afstandKort = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(1).replace('.', ',') : '?';
};

/* Hoe lang de groene knop blijft staan voordat het paneel doorschuift naar de
   put die je net maakte. Lang genoeg om te lezen, kort genoeg om niet op te
   wachten. */
const BEVESTIGING_MS = 900;

function Stip({ soort, groot = false }) {
  const kleur = SOORT_KLEUR[soort] || '#86868b';
  const hol = soort === 'ongeregistreerd';
  const d = groot ? 11 : 9;
  return (
    <span className="pk-stip" style={{
      width: d, height: d,
      background: hol ? '#fff' : kleur,
      boxShadow: hol ? `inset 0 0 0 2.5px ${kleur}` : 'none',
    }} />
  );
}

/* Regel in het paneel: label links, waarde rechts. Geen scheidingslijnen —
   een lijst van vier gegevens heeft geen tabelvorm nodig. */
function Regel({ label, children, stapel = false }) {
  return (
    <dl className={`pk-rij-dl ${stapel ? 'is-gestapeld' : ''}`}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </dl>
  );
}

function Leeg({ tekst = '—' }) {
  return <span className="pk-leeg">{tekst}</span>;
}

/* Een waarde die je mag aanpassen, zonder dat er een formulier op het paneel
   staat. De regel blijft een regel: het getal krijgt een potlood erachter, en
   pas als je daarop klikt wordt het een veldje met een OK-knop. Zo staat er
   niets extra's zolang je niets wijzigt.

   Overgenomen uit het meldingsformulier, waar dit precies dezelfde taak doet:
   één waarde bijstellen in een paneel dat verder alleen leest. */
function Bewerkbaar({ waarde, eenheid, plaatshouder = 'niet opgegeven', getal = true, breed = 78, onOpslaan }) {
  const [bewerken, setBewerken] = useState(false);
  const [ruw, setRuw] = useState('');
  const veld = useRef(null);

  useEffect(() => { if (bewerken) { veld.current?.focus(); veld.current?.select(); } }, [bewerken]);

  const begin = () => {
    setRuw(waarde == null ? '' : String(waarde).replace('.', ','));
    setBewerken(true);
  };

  const bewaar = () => {
    const t = ruw.trim();
    if (t === '') { onOpslaan(''); setBewerken(false); return; }
    if (!getal) { onOpslaan(t); setBewerken(false); return; }
    const n = parseFloat(t.replace(',', '.'));
    if (!Number.isFinite(n) || n <= 0) return;
    onOpslaan(String(n));
    setBewerken(false);
  };

  if (bewerken) {
    return (
      <span className="pk-waarde">
        <input ref={veld} className="pk-mini-invoer" style={{ width: breed }} value={ruw}
          inputMode={getal ? 'decimal' : 'text'}
          onChange={(e) => setRuw(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); bewaar(); }
            if (e.key === 'Escape') { e.preventDefault(); setBewerken(false); }
          }} />
        <button className="pk-ok" onClick={bewaar}>OK</button>
      </span>
    );
  }

  const tekst = getal ? meters(waarde) : (waarde || null);
  return (
    <span className="pk-waarde">
      {tekst == null || tekst === ''
        ? <Leeg tekst={plaatshouder} />
        : <span>{tekst}{eenheid && <span className="pk-eenheid">{eenheid}</span>}</span>}
      <button className="pk-potlood" onClick={begin} title="Aanpassen" aria-label="Aanpassen">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
        </svg>
      </button>
    </span>
  );
}

/* Drie pogingen met oplopende pauze. Voor de twee lookups die dit paneel doet
   — PDOK voor het perceel, de keurkaart voor de maximale boordiepte — en die
   allebei een veld vullen dat de behandelaar niet zelf kan invullen. Mislukt
   het alsnog, dan blijft het veld leeg; liever leeg dan verzonnen. */
const wacht = (ms) => new Promise((r) => setTimeout(r, ms));
async function metHerkansing(doe, pogingen = 3) {
  for (let i = 0; i < pogingen; i += 1) {
    try {
      return await doe();
    } catch {
      if (i === pogingen - 1) return null;
      await wacht(400 * (i + 1));
    }
  }
  return null;
}

/* Welke van de twee keurkaartlagen op de putrij terechtkomt.

   te boren        de laag die bij het gekozen doel hoort — de put wordt
                   immers voor dát doel geboord.
   gerealiseerd    altijd laag 12. Van een put die er al staat weten we vaak
                   niet waarvoor of wanneer hij is geboord; dan is de ruimste
                   van de twee de eerlijkste keuze.
   uit het register  niets. Die rij bestaat al en wordt niet overschreven.

   Beide waarden blijven op de put staan, want in het paneel worden ze allebei
   getoond als naslag. */
const dieptesVoor = (put, d11, d12, bijlage, uitDoel = true) => {
  const velden = { max_diepte_11: d11, max_diepte_12: d12 };
  if (!put || put.uitRegister) return velden;
  /* Stuurt het doel de diepte niet, dan vullen we niets in: bij een vergunning
     staat de diepte in de beschikking en zou een automatisch getal de
     behandelaar een waarde in de mond leggen. De twee lagen blijven wel staan,
     als naslag naast de kaart. */
  if (!uitDoel) return velden;
  /* Een diepte die er al staat blijft staan. Bij een put uit een klaarstaande
     melding is dat de waarde die bij het vastleggen gold; die opnieuw afleiden
     zou de inhoud van een melding kunnen veranderen. En haperde de lookup, dan
     zou een leeg antwoord de bestaande waarde wissen — waarna de melding niet
     meer op te slaan is en niemand ziet waarom. */
  if (String(put.max_diepte_mv || '').trim()) return velden;
  const nieuw = put.soort === 'nieuw' ? (bijlage === 11 ? d11 : d12) : d12;
  if (nieuw !== '') velden.max_diepte_mv = nieuw;
  return velden;
};

/* Sleutel van een gebied. Zelfde afspraak als in RDKaart, zodat een pil hier
   het bijbehorende vlak daar kan aanwijzen. */
export const gebiedSleutel = (g) => String(g.gebied_id ?? `${g.type}|${g.naam || ''}`);

/* Waar de put ligt, als pil in de kleur van de kaartlaag. Wijs je de pil aan,
   dan springt het vlak op de kaart naar voren — een label zonder plek zegt
   weinig als je niet weet welk van de vlakken bedoeld wordt. */
function GebiedPil({ gebied, onAanwijzen }) {
  const s = gebiedStijl(gebied.type);
  return (
    <span className="pk-gebiedpil"
      style={{ background: s.chipBg, color: s.chipText, borderColor: s.stroke }}
      title={gebied.naam}
      onMouseEnter={() => onAanwijzen && onAanwijzen(gebiedSleutel(gebied))}
      onMouseLeave={() => onAanwijzen && onAanwijzen(null)}>
      {s.kort || s.label || gebied.naam}
    </span>
  );
}

function GebiedPillen({ lijst, onAanwijzen }) {
  if (!lijst || !lijst.length) return null;
  return (
    <div className="pk-gebiedpillen">
      {lijst.map((g) => (
        <GebiedPil key={gebiedSleutel(g)} gebied={g} onAanwijzen={onAanwijzen} />
      ))}
    </div>
  );
}

/* Ligt de put in een of meer waterstaatswerken, dan is daar één vergunning
   voor: het boren en behouden van díe put. Vandaar één besluitnummer, en niet
   een nummer per werk.

   Eigen kaartje onder de putgegevens, in grijs: het is naslag en geen
   registratiegegeven. Niet verplicht — een ontbrekend nummer mag de
   registratie niet tegenhouden. */
function VergunningKaart({ gebieden, besluitnummer, onAanwijzen, onBesluitnummer }) {
  if (!gebieden || !gebieden.length) return null;
  return (
    <div className="pk-verg">
      <p className="pk-verg-kop">Vergunning waterstaatswerken</p>
      <GebiedPillen lijst={gebieden} onAanwijzen={onAanwijzen} />
      <dl className="pk-rij-dl pk-verg-rij">
        <dt>Besluitnummer</dt>
        <dd>
          {/* Geen onOpslaan betekent: hier valt niets vast te leggen. Dan is
              leestekst eerlijker dan een invulveld dat bij het opslaan nergens
              heen gaat. */}
          {onBesluitnummer
            ? <Bewerkbaar waarde={besluitnummer} getal={false} breed={124}
                plaatshouder="niet opgegeven" onOpslaan={onBesluitnummer} />
            : (besluitnummer || <Leeg />)}
        </dd>
      </dl>
    </div>
  );
}

/* De volgorde van de lijst: op perceel, dan van noord naar zuid. Op volgorde
   van toevoegen springt je oog bij het langslopen kriskras over de kaart. */
const perceelVergelijker = new Intl.Collator('nl', { numeric: true, sensitivity: 'base' });
function opVolgorde(putten) {
  return [...putten].sort((a, b) => {
    const pa = a.perceel || '', pb = b.perceel || '';
    if (pa && !pb) return -1;
    if (!pa && pb) return 1;
    if (pa && pb) {
      const v = perceelVergelijker.compare(pa, pb);
      if (v !== 0) return v;
    }
    if (a.y_rd !== b.y_rd) return b.y_rd - a.y_rd;
    return a.x_rd - b.x_rd;
  });
}

export default function PuttenKaart({
  putten,
  /* Moet de setter van een useState zijn: er wordt met een functie bijgewerkt.
     Twee lookups die tegelijk terugkomen bouwen anders allebei op dezelfde
     oude lijst, en dan wint de laatste en verdwijnt het antwoord van de eerste. */
  onChange,
  registerPutten = [],
  gebieden = [],
  bijlage = null,
  hoogte = 520,
  matchStraal = 10,
  /* Blokkeren kwetsbare gebieden hier? Bij een melding wel, bij een vergunning
     niet: daar heeft het bevoegd gezag de afweging al gemaakt. Uit betekent
     niet "negeren" maar "als label tonen in plaats van als weigering". */
  kwetsbaarBlokkeert = true,
  /* Bepaalt het doel de maximale boordiepte? Bij een melding wel — dan is de
     keurkaartlaag een gevolg van het doel en kan er geen put zijn vóór die
     keuze. Bij een vergunning niet: dan zijn beide lagen naslag en vult de
     behandelaar de diepte uit de beschikking in. */
  dieptesUitDoel = true,
  /* Mag een put die al in het register staat aan de selectie worden
     toegevoegd? Bij een melding en een wijzigingsbesluit wel — daar is het
     hele punt dat je bestaande putten aan een recht hangt.

     Op de pagina "Nieuwe put" niet: daar is geen inrichting om aan te koppelen,
     dus zou toevoegen niets doen. Uit betekent niet "onzichtbaar": de
     registerputten blijven op de kaart staan en je krijgt bij een klik nog
     steeds hun kaartje te zien, alleen zonder knop. Zien waar ze liggen is
     juist waarom ze er zijn — anders prik je een tweede put bovenop een
     bestaande. */
  koppelenKan = true,
  /* Bij een wijzigingsbesluit: de putten die nu aan de inrichting hangen mogen
     eruit gehaald worden. Ze verdwijnen dan niet uit beeld maar worden grijs,
     met de datum waarop het gebeurt. */
  verwijderbaar = false,
  verwijderDatum = null,
}) {
  /* Wat er in de kolom staat. null = de lijst. */
  const [paneel, setPaneel] = useState(null);
  const [netToegevoegd, setNetToegevoegd] = useState(null);
  /* Het gebied dat op de kaart even mag opvallen, en de lagen die aangezet
     moeten worden. Allebei gestuurd vanuit dit paneel: de kaart weet niet welk
     label je aanwijst en de legenda weet niet waar je klikt. */
  const [aangewezen, setAangewezen] = useState(null);
  /* De put waar de muis in de lijst overheen staat, en de put waar de kaart
     naartoe moet schuiven. Twee dingen, twee stukjes state: zweven mag de
     kaart niet verplaatsen, en klikken moet ook werken als je daarna met de
     muis weggaat. */
  const [zweefPut, setZweefPut] = useState(null);
  const [panDoel, setPanDoel] = useState(null);
  const [legendaAan, setLegendaAan] = useState(null);

  const puttenRef = useRef(putten);
  puttenRef.current = putten;

  const gekozenIds = useMemo(
    () => new Set(putten.filter((p) => p.put_id).map((p) => p.put_id)), [putten]);

  /* Alleen putten die hier geregistreerd mogen worden. Bij een melding vallen
     de putten in kwetsbare gebieden af; bij een vergunning niet. */
  const meldbaarRegister = useMemo(
    () => (kwetsbaarBlokkeert ? registerPutten.filter(isMeldbaar) : registerPutten),
    [registerPutten, kwetsbaarBlokkeert]);

  /* Waar een punt in ligt, als label. Het ambtsgebied valt altijd af — dat is
     het hele werkgebied en zegt niets. Kwetsbare gebieden vallen alleen af als
     ze blokkeren: dan staan ze al bij de weigering, en zou een tweede
     vermelding als pil de indruk wekken dat het gewoon mag. */
  const liggingOp = useCallback((x, y) => gebiedenOpPunt(x, y, gebieden)
    .filter((g) => g.type !== 'ambtsgebied'
      && !(kwetsbaarBlokkeert && KWETSBARE_TYPES.includes(g.type))),
  [gebieden, kwetsbaarBlokkeert]);

  /* Alleen de waterstaatswerken. Dáár hoort het besluitnummer van de
     omgevingsvergunning bij; een kwetsbaar gebied is geen werk waarvoor een
     vergunning wordt afgegeven, dus dat hoort niet onder dat kopje te staan —
     ook niet als het hier verder gewoon getoond mag worden. */
  const waterstaatswerkenOp = useCallback((x, y) => gebiedenOpPunt(x, y, gebieden)
    .filter((g) => g.type !== 'ambtsgebied' && !KWETSBARE_TYPES.includes(g.type)),
  [gebieden]);

  // Waar de put verder in ligt, zonder dat er een vergunningvraag aan hangt.
  const kwetsbaarOp = useCallback((x, y) => gebiedenOpPunt(x, y, gebieden)
    .filter((g) => KWETSBARE_TYPES.includes(g.type)),
  [gebieden]);

  /* Voor de aanwijzer: dezelfde weigering als bij een klik, maar dan tientallen
     keren per seconde. Vandaar de voorgekauwde variant met omhullende
     rechthoeken en geheugen voor het laatste antwoord. */
  const kwetsbaarToets = useMemo(() => maakKwetsbaarToets(gebieden), [gebieden]);
  const verbodenOpPunt = useCallback(
    (x, y) => (kwetsbaarBlokkeert ? !!kwetsbaarToets(x, y) : false),
    [kwetsbaarToets, kwetsbaarBlokkeert]);

  const wijzig = (sleutel, velden) =>
    onChange((huidig) => huidig.map((p) => (p.sleutel === sleutel ? { ...p, ...velden } : p)));

  const verwijder = (sleutel) => {
    onChange((huidig) => huidig.filter((p) => p.sleutel !== sleutel));
    setPaneel(null);
  };

  /* Wisselt het doel, dan wisselt de keurkaartlaag en daarmee welke van de
     twee dieptes voor een te boren put geldt. Beide waarden staan al op de put
     — ze worden bij het toevoegen in één keer opgehaald — dus dit is alleen
     omschakelen, geen nieuwe ronde langs de keurkaart.

     Alleen te boren putten volgen het doel. Een gerealiseerde put houdt laag 12:
     van een put die er al staat weten we vaak niet waarvoor of wanneer hij is
     geboord, en dan is de ruimste van de twee de eerlijkste. */
  const vorigeBijlage = useRef(bijlage);
  useEffect(() => {
    if (vorigeBijlage.current === bijlage) return;
    vorigeBijlage.current = bijlage;
    if (!bijlage) return;
    onChange((huidig) => {
      const om = huidig.map((p) => (boorstatusVan(p) === 'te_boren'
        ? { ...p, max_diepte_mv: bijlage === 11 ? (p.max_diepte_11 || '') : (p.max_diepte_12 || '') }
        : p));
      // Dezelfde lijst teruggeven betekent voor React: niets veranderd.
      return om.some((p, i) => p !== huidig[i]) ? om : huidig;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bijlage]);

  /* Putten die niet via dit paneel zijn toegevoegd — overgenomen uit de
     lopende registratie, of ingelezen uit een bijlage — hebben de twee
     keurkaartwaarden nog niet. Die halen we hier alsnog op, één keer per put.
     De ref onthoudt welke al langs zijn geweest; zonder dat blijft dit effect
     zichzelf opnieuw aanroepen omdat het de puttenlijst wijzigt. */
  const dieptesGehaald = useRef(new Set());
  useEffect(() => {
    if (dieptesUitDoel && !bijlage) return;
    putten
      .filter((p) => p.max_diepte_11 === undefined && !dieptesGehaald.current.has(p.sleutel))
      .forEach((p) => {
        dieptesGehaald.current.add(p.sleutel);
        vulDieptes(p.sleutel, +p.x_rd, +p.y_rd);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [putten, bijlage]);

  // De flits op het kaartje in de lijst dooft vanzelf.
  useEffect(() => {
    if (!netToegevoegd) return undefined;
    const t = setTimeout(() => setNetToegevoegd(null), 2600);
    return () => clearTimeout(t);
  }, [netToegevoegd]);

  /* De twee maximale boordieptes bij een punt. Allebei de keurkaartlagen in
     één keer: welke van de twee geldt hangt af van het doel, en dat kan daarna
     nog wisselen. Zo hoeft er bij een doelwissel niets opgehaald te worden.

     Het perceel komt van PDOK. Dat hoort er te zijn, en een enkele hapering mag
     geen lege regel opleveren die niemand meer kan invullen — het veld is niet
     bewerkbaar. Vandaar drie pogingen met oplopende pauze, voor allebei. */
  const vulDieptes = async (sleutel, x, y) => {
    const [elf, twaalf] = await Promise.all([
      metHerkansing(() => getKeurkaartCheck(x, y, 11)),
      metHerkansing(() => getKeurkaartCheck(x, y, 12)),
    ]);
    const d11 = elf?.data?.max_diepte_m != null ? String(elf.data.max_diepte_m) : '';
    const d12 = twaalf?.data?.max_diepte_m != null ? String(twaalf.data.max_diepte_m) : '';
    const put = puttenRef.current.find((p) => p.sleutel === sleutel);
    if (put) wijzig(sleutel, dieptesVoor(put, d11, d12, bijlage, dieptesUitDoel));
  };

  /* De twee keurkaartlagen bij het punt dat in de kolom staat. Voor een lege
     plek en voor een registerput dezelfde vraag, dus één helper. Alleen waar de
     dieptes naslag zijn: bij een melding geldt er maar één van de twee en zou
     dit twee getallen tonen waarvan er één niet van toepassing is. */
  const haalDieptesVoorPaneel = async (x, y, soort) => {
    const [elf, twaalf] = await Promise.all([
      metHerkansing(() => getKeurkaartCheck(x, y, 11)),
      metHerkansing(() => getKeurkaartCheck(x, y, 12)),
    ]);
    setPaneel((huidig) => (huidig && huidig.soort === soort
      ? {
        ...huidig,
        diepte11: elf?.data?.max_diepte_m != null ? String(elf.data.max_diepte_m) : '',
        diepte12: twaalf?.data?.max_diepte_m != null ? String(twaalf.data.max_diepte_m) : '',
      }
      : huidig));
  };

  const vulPerceel = async (sleutel, x, y) => {
    const r = await metHerkansing(() => lookupPerceel(x, y));
    if (r?.data?.perceel) wijzig(sleutel, { perceel: r.data.perceel });
  };

  const toonNieuw = (sleutel) => {
    setNetToegevoegd(sleutel);
    setTimeout(() => setPaneel({ soort: 'put', sleutel }), BEVESTIGING_MS);
  };

  const koppel = (rp, herkomst) => {
    if (gekozenIds.has(rp.put_id)) return;
    const sleutel = nieuweSleutel();
    onChange((huidig) => [...huidig, {
      sleutel, soort: 'gekoppeld', put_id: rp.put_id, uitRegister: true,
      x_rd: +rp.x_rd, y_rd: +rp.y_rd, perceel: rp.perceel || null,
      max_diepte_mv: rp.max_diepte_mv != null ? String(rp.max_diepte_mv) : '',
      opgegeven_diepte_m: rp.opgegeven_diepte_m != null ? String(rp.opgegeven_diepte_m) : '',
      /* Stond er in het register nog geen werkelijke diepte? Dan mag je hem
         hier invullen. Onthouden bij het koppelen, want zodra je iets typt is
         het veld niet meer leeg en zou de vraag zichzelf beantwoorden. */
      opgegevenDiepteLeeg: rp.opgegeven_diepte_m == null,
      /* Wat het register over de boorstatus weet, naast wat de melder (of de
         behandelaar namens hem) straks zegt. Zie boorstatusVan. */
      boorstatus: null,
      registerStatus: rp.boorstatus_code || null,
      registerDiepte: rp.opgegeven_diepte_m ?? null,
      gemetenDiepte: rp.gemeten_diepte_m ?? null,
      besluitnummer: rp.besluitnummer_wsw || '',
      kandidaten: [{ ...rp, afstand_meter: 0 }], herkomst,
    }]);
    toonNieuw(sleutel);
    return sleutel;
  };

  const voegToe = (x, y, soort, kandidaten, herkomst) => {
    const sleutel = nieuweSleutel();
    onChange((huidig) => [...huidig, {
      sleutel, soort, put_id: null, uitRegister: false, x_rd: x, y_rd: y,
      perceel: null, max_diepte_mv: '', opgegeven_diepte_m: '',
      besluitnummer: '',
      kandidaten, herkomst,
    }]);
    toonNieuw(sleutel);
    vulPerceel(sleutel, x, y);
    return sleutel;
  };

  /* Eén ingang voor alle manieren om een plek aan te wijzen: klikken op de
     kaart en het intypen van een coördinaat komen hier allebei binnen. */
  const opPlek = async (x, y, herkomst) => {
    /* In een kwetsbaar gebied kan geen put worden opgevoerd.

       Bij een klik op de kaart gebeurt er dan niets: de aanwijzer laat daar al
       een verbodsteken zien, dus het antwoord staat er voordat je klikt. Bij een
       ingetypt coördinaat is er geen aanwijzer geweest — dan zou "er gebeurt
       niets" lezen als een kapot invoerveld. Daar tonen we de locatiegegevens
       wél, met de gebieden erbij, alleen zonder knop om iets toe te voegen. */
    const kwetsbaar = kwetsbaarBlokkeert ? kwetsbaarOpPunt(x, y, gebieden) : null;
    if (kwetsbaar && herkomst !== 'coordinaat') return;

    const exact = exacteTreffer(x, y, meldbaarRegister);
    if (exact) {
      if (gekozenIds.has(exact.put_id)) {
        const bestaand = puttenRef.current.find((p) => p.put_id === exact.put_id);
        if (bestaand) setPaneel({ soort: 'put', sleutel: bestaand.sleutel });
        return;
      }
      /* Kan er niet gekoppeld worden, dan is een klik bovenop een bestaande put
         een kijkactie: laat zijn kaartje zien in plaats van hem stilzwijgend
         aan de selectie toe te voegen. Zelfde uitkomst als een klik op de
         grijze stip zelf. */
      if (!koppelenKan) {
        setPaneel({ soort: 'register', rp: exact });
        if (!dieptesUitDoel) haalDieptesVoorPaneel(+exact.x_rd, +exact.y_rd, 'register');
        return;
      }
      koppel(exact, herkomst);
      return;
    }

    const kandidaten = kwetsbaar ? [] : zoekKandidaten(x, y, meldbaarRegister, matchStraal)
      .filter((k) => !gekozenIds.has(k.put_id));
    /* Waterstaatswerken blokkeren niets — handmatig mag daar een put bij. Ze
       staan er alleen bij als pil, zodat je weet waar je 'm neerzet.

       Bij een weigering hoort het kwetsbare gebied er als label bij: dát is de
       reden dat er geen knop staat. */
    const ligging = kwetsbaar
      ? gebiedenOpPunt(x, y, gebieden).filter((g) => g.type !== 'ambtsgebied')
      : liggingOp(x, y);
    /* Ligt de plek in een waterstaatswerk, zet die laag dan aan. Anders lees je
       in het paneel dat de put in een regionale kering ligt terwijl je op de
       kaart geen kering ziet — de laag stond uit. */
    if (ligging.length) {
      setLegendaAan({ types: [...new Set(ligging.map((g) => g.type))], nonce: Date.now() });
    }
    setPaneel({
      soort: 'plek', x, y, kandidaten, herkomst,
      geweigerd: !!kwetsbaar, ligging, perceel: null, bezig: true,
    });
    /* Naast het perceel ook de twee keurkaartlagen, maar alleen waar ze naslag
       zijn. Bij een melding volgt de diepte uit het doel en zou dit twee getallen
       tonen waarvan er één niet geldt. */
    if (!dieptesUitDoel) haalDieptesVoorPaneel(x, y, 'plek').catch(() => {});
    try {
      const r = await lookupPerceel(x, y);
      setPaneel((huidig) => (huidig && huidig.soort === 'plek' && huidig.x === x && huidig.y === y
        ? { ...huidig, perceel: r?.data?.perceel || null, bezig: false } : huidig));
    } catch {
      setPaneel((huidig) => (huidig && huidig.soort === 'plek' ? { ...huidig, bezig: false } : huidig));
    }
  };

  const actieveSleutel = paneel?.soort === 'put' ? paneel.sleutel : null;

  const kaartPunten = useMemo(() => {
    const register = meldbaarRegister
      .filter((rp) => !gekozenIds.has(rp.put_id))
      .map((rp) => ({
        put_id: rp.put_id, x_rd: rp.x_rd, y_rd: rp.y_rd, perceel: rp.perceel,
        afgedicht: true,
        // Ook een registerput die je aankijkt hoort op de kaart aangewezen te
        // zijn; anders zoek je welke van de grijze stippen je net raakte.
        geselecteerd: paneel?.soort === 'register' && paneel.rp.put_id === rp.put_id,
        onClick: () => {
          setPaneel({ soort: 'register', rp });
          if (!dieptesUitDoel) haalDieptesVoorPaneel(+rp.x_rd, +rp.y_rd, 'register');
        },
      }));
    const eigen = putten.map((p) => ({
      put_id: p.put_id || SOORT_NAAM[p.soort],
      x_rd: p.x_rd, y_rd: p.y_rd, perceel: p.perceel,
      soort: p.verwijderd ? 'verwijderd' : (boorstatusVan(p) === 'te_boren' ? 'nieuw' : p.soort),
      geselecteerd: p.sleutel === actieveSleutel,
      onClick: () => setPaneel({ soort: 'put', sleutel: p.sleutel }),
    }));
    // De plek waar je net klikte staat er ook op, zolang de vraag openstaat.
    /* Zodra er gekozen is verdwijnt de hangende ring: de echte marker staat er
       dan al, en twee tekens op dezelfde plek is er één te veel. */
    const hangend = paneel?.soort === 'plek' && !paneel.gedaan
      ? [{ put_id: 'Nieuwe plek', x_rd: paneel.x, y_rd: paneel.y, soort: 'onbeslist', geselecteerd: true }]
      : [];
    return [...register, ...eigen, ...hangend];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [putten, meldbaarRegister, gekozenIds, actieveSleutel, paneel]);

  /* Geen puntlegenda meer onder de kaart. Wat blauw, oranje en hol betekenen
     staat al in de lijst ernaast, met dezelfde stippen; en dat een grijze stip
     een registerput is die je kunt koppelen zegt het menu achter "Put
     toevoegen" al. Twee keer hetzelfde uitleggen maakt de kaart alleen langer. */

  const focus = actieveSleutel ? putten.filter((p) => p.sleutel === actieveSleutel) : null;

  /* Zonder doel geen putten. De maximale boordiepte komt van de keurkaart, en
     welke van de twee kaartlagen geldt hangt af van het doel: bijlage 11 voor
     grasland, bijlage 12 voor de rest. Een put toevoegen voordat dat vaststaat
     levert een diepte op die je daarna stilletjes moet corrigeren. */
  /* Zonder de gebiedsgegevens kan niet worden beoordeeld of hier een put mag.
     "Ik weet het niet" mag dan niet hetzelfde uitpakken als "het mag": dan zou
     een trage of mislukte lading stilzwijgend de weigering uitschakelen. */
  if (!gebieden.length) {
    return (
      <div className="pk">
        <div className="pk-kop">
          <h3 className="pk-titel">Putten</h3>
        </div>
        <div className="pk-slot">
          <span className="spinner" /> Gebiedsgegevens worden geladen. Zolang die
          er niet zijn kan niet worden bepaald of een put op een plek is
          toegestaan.
        </div>
      </div>
    );
  }

  if (dieptesUitDoel && !bijlage) {
    return (
      <div className="pk">
        <div className="pk-kop">
          <h3 className="pk-titel">Putten</h3>
        </div>
        <div className="pk-slot">
          Kies eerst een doel. Dat bepaalt welke keurkaart geldt, en daarmee de
          maximale boordiepte van de putten.
        </div>
      </div>
    );
  }

  return (
    <div className="pk">
      <div className="pk-kop">
        <h3 className="pk-titel">Putten</h3>
        <span className="pk-aantal">{putten.length}</span>
        <span className="pk-kop-vul" />
        <ToevoegMenu
          registerPutten={meldbaarRegister}
          gekozenIds={gekozenIds}
          onKoppel={koppelenKan ? (rp) => koppel(rp, 'zoek') : null}
          onCoordinaat={(x, y) => opPlek(Math.round(x), Math.round(y), 'coordinaat')}
        />
      </div>

      <div className="pk-grid">
        <div className="pk-kaartvak">
          <KaartMetLegenda
            punten={kaartPunten}
            overlays={gebieden}
            hoogte={hoogte}
            fitBijElkeUpdate={false}
            onKaartClick={(x, y) => opPlek(Math.round(x), Math.round(y), 'kaart')}
            focusPunten={focus}
            benadrukt={aangewezen ? [aangewezen] : null}
            draadkruis={zweefPut}
            panNaar={panDoel}
            verbodenOpPunt={verbodenOpPunt}
            legendaZetAan={legendaAan}
          />
        </div>

        <div className="pk-kolom" style={{ height: hoogte }}>
          {paneel === null ? (
            <Lijst
              putten={putten}
              netToegevoegd={netToegevoegd}
              verwijderDatum={verwijderDatum}
              onZweef={(p) => setZweefPut(p ? { x_rd: p.x_rd, y_rd: p.y_rd } : null)}
              onKies={(sleutel) => {
                const p = putten.find((q) => q.sleutel === sleutel);
                if (p) setPanDoel({ x_rd: p.x_rd, y_rd: p.y_rd, nonce: sleutel + Math.random() });
                setPaneel({ soort: 'put', sleutel });
              }}
            />
          ) : (
            /* De Lijst-knop staat bóven de kaart, niet erin: hij gaat niet over
               de put die je bekijkt maar over de kolom. En de kaart hugt zijn
               inhoud — een halfleeg vak van 520 px leest als een fout. */
            <div className="pk-paneel">
              <button className="pk-terug" onClick={() => { setZweefPut(null); setPaneel(null); }}>
                <span className="pk-terug-pijl" aria-hidden="true" /> Lijst
              </button>
              <div className="pk-paneel-kaart">
                {paneel.soort === 'put' && (
                  <PutPaneel
                    put={putten.find((p) => p.sleutel === paneel.sleutel)}
                    bijlage={bijlage}
                    dieptesUitDoel={dieptesUitDoel}
                    verwijderbaar={verwijderbaar}
                    verwijderDatum={verwijderDatum}
                    besluitnummerVast={!koppelenKan}
                    liggingOp={waterstaatswerkenOp}
                    kwetsbaarOp={kwetsbaarBlokkeert ? null : kwetsbaarOp}
                    onAanwijzen={setAangewezen}
                    onWijzig={wijzig}
                    onVerwijder={verwijder}
                  />
                )}
                {paneel.soort === 'plek' && (
                  <PlekPaneel
                    plek={paneel}
                    onAanwijzen={setAangewezen}
                    onKoppel={(k) => { setPaneel({ ...paneel, gedaan: k.put_id }); koppel(k, paneel.herkomst); }}
                    onSoort={(soort) => {
                      setPaneel({ ...paneel, gedaan: soort });
                      voegToe(paneel.x, paneel.y, soort, paneel.kandidaten, paneel.herkomst);
                    }}
                  />
                )}
                {paneel.soort === 'register' && (
                  <RegisterPaneel rp={paneel.rp} gedaan={paneel.gedaan} liggingOp={liggingOp}
                    diepte11={paneel.diepte11} diepte12={paneel.diepte12}
                    onAanwijzen={setAangewezen}
                    onKoppel={koppelenKan
                      ? () => { setPaneel({ ...paneel, gedaan: true }); koppel(paneel.rp, 'kaart'); }
                      : null} />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── De lijst ─────────────────────────────────────────────────────────────
   Kaartjes, geen tabelrijen: één regel met waar de put ligt en op welk
   perceel. Alles wat er verder over te zeggen valt staat in het paneel dat
   opengaat als je erop klikt. */
function Lijst({ putten, netToegevoegd, onKies, onZweef, verwijderDatum }) {
  if (putten.length === 0) {
    return (
      <div className="pk-leegstand">
        Nog geen putten.<br />Klik op de kaart om er een toe te voegen.
      </div>
    );
  }
  return (
    <ul className="pk-lijst">
      {opVolgorde(putten).map((p) => (
        <li key={p.sleutel} className={[
          netToegevoegd === p.sleutel ? 'is-nieuw' : '',
          p.verwijderd ? 'is-eruit' : '',
        ].filter(Boolean).join(' ')}>
          {/* Zweven wijst aan, klikken gaat erheen. Ook op focus, zodat wie
              met het toetsenbord door de lijst loopt hetzelfde ziet. */}
          <button
            className="pk-rij"
            onClick={() => onKies(p.sleutel)}
            onMouseEnter={() => onZweef && onZweef(p)}
            onMouseLeave={() => onZweef && onZweef(null)}
            onFocus={() => onZweef && onZweef(p)}
            onBlur={() => onZweef && onZweef(null)}
          >
            <Stip soort={stipSoort(p)} groot />
            <span className="pk-rij-tekst">
              <span className="pk-rij-titel">
                {p.put_id
                  ? <span className="mono">{fmtId(p.put_id)}</span>
                  : SOORT_NAAM[p.soort]}
              </span>
              <span className="pk-rij-sub">
                {p.verwijderd
                  ? `Gaat eruit${verwijderDatum ? ` op ${verwijderDatum}` : ''}`
                  : <>
                    <span className="mono">{Math.round(p.x_rd)}, {Math.round(p.y_rd)}</span>
                    {p.perceel ? ` · ${p.perceel}` : ''}
                  </>}
              </span>
            </span>
            <span className="pk-chevron" aria-hidden="true" />
          </button>
        </li>
      ))}
    </ul>
  );
}

/* De maximale boordiepte uit de keurkaart.

   Bij een put die nog geboord moet worden is er één antwoord: de laag die bij
   het gekozen doel hoort. Bij een put die er al is staan beide lagen er als
   naslag — welke er destijds gold weten we niet, en het verschil tussen de twee
   zegt iets over wat er op die plek mag.

     G/K   bijlage 11, grasland en kleine onttrekkingen
     O     bijlage 12, overige onttrekkingen en gewasberegening */
function Dieptes({ put, bijlage, uitDoel = true, onWijzig }) {
  const regel = (label, waarde) => (
    <Regel label={label}>
      {meters(waarde)
        ? <>{meters(waarde)}<span className="pk-eenheid">m</span></>
        : <Leeg />}
    </Regel>
  );
  if (!uitDoel) {
    /* Vergunning: de twee keurkaartlagen als naslag, en daarboven de diepte
       die in de beschikking staat — die is hier het antwoord, niet de kaart. */
    return (
      <>
        <Regel label="Maximale boordiepte">
          {put.uitRegister
            ? (meters(put.max_diepte_mv)
              ? <>{meters(put.max_diepte_mv)}<span className="pk-eenheid">m</span></>
              : <Leeg />)
            : <Bewerkbaar waarde={put.max_diepte_mv} eenheid="m"
                onOpslaan={(v) => onWijzig(put.sleutel, { max_diepte_mv: v })} />}
        </Regel>
        {regel('Max. diepte (G/K)', put.max_diepte_11)}
        {regel('Max. diepte (O)', put.max_diepte_12)}
      </>
    );
  }
  if (boorstatusVan(put) === 'te_boren') {
    /* Ook een registerput die de melder als nog te boren aanwijst: dan geldt
       het maximum voor het doel van déze melding, en dat gaat bij het
       klaarzetten meteen de put in. */
    return regel(`Max. diepte (${bijlage === 11 ? 'G/K' : 'O'})`, put.max_diepte_mv);
  }
  return (
    <>
      {regel('Max. diepte (G/K)', put.max_diepte_11)}
      {regel('Max. diepte (O)', put.max_diepte_12)}
    </>
  );
}

/* ── Één put ────────────────────────────────────────────────────────────
   Wat er over deze put te zeggen valt, en het enige gereedschap eraan: de
   diepte bijstellen en 'm weer weghalen. Geen regel die vertelt dat een put
   met een putnummer in het register staat — dat zegt het nummer zelf al. */
function PutPaneel({ put, bijlage, dieptesUitDoel = true, verwijderbaar = false,
  verwijderDatum = null, besluitnummerVast = false,
  liggingOp, kwetsbaarOp, onAanwijzen, onWijzig, onVerwijder }) {
  if (!put) return null;
  const uitRegister = put.uitRegister === true;
  const status = boorstatusVan(put);
  const gerealiseerd = status !== 'te_boren';
  // Aanvullen mag; overschrijven niet. Zie de toelichting bij het veld zelf.
  const magInvullen = uitRegister && !dieptesUitDoel && put.opgegevenDiepteLeeg === true;
  /* Bij een registerput zegt de behandelaar hier namens de melder of de put
     geboord is (papieren melding). Wordt het te boren, dan hoort er het
     maximum voor het doel van deze melding bij; anders het maximum zoals het
     register het had. */
  const registerStatus = put.soort === 'gekoppeld' ? (put.registerStatus || 'onbekend') : null;
  const registerMax = put.kandidaten?.[0]?.max_diepte_mv;
  const kiesStatus = (v) => {
    const claim = v || null;
    const wordtTeBoren = claim === 'te_boren';
    onWijzig(put.sleutel, {
      boorstatus: claim,
      max_diepte_mv: wordtTeBoren
        ? (bijlage === 11 ? (put.max_diepte_11 || '') : (put.max_diepte_12 || ''))
        : (registerMax != null ? String(registerMax) : ''),
    });
  };

  return (
    <>
      <h4 className="pk-paneel-titel">
        <Stip soort={stipSoort(put)} groot />
        {put.put_id
          ? <span className="mono">{fmtId(put.put_id)}</span>
          : SOORT_NAAM[put.soort]}
      </h4>
      {put.verwijderd && (
        <p className="pk-eruit">
          Gaat uit de inrichting{verwijderDatum ? ` op ${verwijderDatum}` : ''}.
        </p>
      )}

      <Regel label="Coördinaten (RD)">
        <span className="mono">{Math.round(put.x_rd)}, {Math.round(put.y_rd)}</span>
      </Regel>
      {/* Perceel en maximale boordiepte zijn afgeleid — PDOK en de keurkaart —
          dus leestekst, geen invoerveld. Overtypen zou een waarde opleveren die
          bij de volgende lookup weer anders is. */}
      <Regel label="Perceel">{put.perceel || <Leeg />}</Regel>
      <Regel label="Boorstatus" stapel={!!(registerStatus && put.boorstatus && put.boorstatus !== registerStatus)}>
        {put.soort === 'gekoppeld' && dieptesUitDoel ? (
          <>
            <select className="pk-status-keuze" value={put.boorstatus || ''}
              onChange={(e) => kiesStatus(e.target.value)}>
              <option value="">{STATUS_TEKST[registerStatus]} (register)</option>
              <option value="geboord">Geboord</option>
              <option value="te_boren">Nog te boren</option>
            </select>
            {put.boorstatus && put.boorstatus !== registerStatus && (
              <span className="pk-was">was: {STATUS_TEKST[registerStatus].toLowerCase()}</span>
            )}
          </>
        ) : (
          <span className={status === 'onbekend' ? 'pk-leeg' : ''}>{STATUS_TEKST[status]}</span>
        )}
      </Regel>
      <Dieptes put={put} bijlage={bijlage} uitDoel={dieptesUitDoel} onWijzig={onWijzig} />
      {/* Een put die nog geboord moet worden heeft geen diepte; die regel zou
          alleen maar leeg staan. */}
      {gerealiseerd && put.gemetenDiepte != null && (
        <Regel label="Gemeten diepte">
          {meters(put.gemetenDiepte)}<span className="pk-eenheid">m</span>
        </Regel>
      )}
      {gerealiseerd && put.gemetenDiepte == null && (
        <Regel label="Opgegeven diepte">
          {/* Een registerput blijft leestekst — behalve als het register de
              diepte nog niet kent en dit geen melding is. Dan vul je 'm hier in
              in plaats van een omweg langs de putpagina te maken. Een waarde
              die er al staat wijzigen doe je wél daar: dat is een correctie op
              het register en die hoort gearchiveerd te worden. */}
          {uitRegister && !(magInvullen)
            ? (meters(put.opgegeven_diepte_m) ? <>{meters(put.opgegeven_diepte_m)}<span className="pk-eenheid">m</span></> : <Leeg />)
            : <Bewerkbaar waarde={put.opgegeven_diepte_m} eenheid="m"
                onOpslaan={(v) => onWijzig(put.sleutel, { opgegeven_diepte_m: v })} />}
        </Regel>
      )}

      {/* Waar de put ligt, als het gebied geen vergunningvraag oproept. Bij een
          melding komt dit niet voor: daar kan geen put in een kwetsbaar gebied
          staan. */}
      {kwetsbaarOp && kwetsbaarOp(put.x_rd, put.y_rd).length > 0 && (
        <Regel label="Gelegen in">
          <GebiedPillen lijst={kwetsbaarOp(put.x_rd, put.y_rd)} onAanwijzen={onAanwijzen} />
        </Regel>
      )}

      <VergunningKaart
        gebieden={liggingOp(put.x_rd, put.y_rd)}
        onAanwijzen={onAanwijzen}
        besluitnummer={put.besluitnummer}
        /* Bij een put uit het register hangt het besluitnummer aan de keuze die
           de behandelaar in dít scherm maakt, en die keuze wordt bewaard bij het
           recht. Is er geen recht om hem aan te hangen — de pagina "Nieuwe put"
           — dan is het nummer wat het register al weet, en verder niets. */
        onBesluitnummer={besluitnummerVast && put.uitRegister
          ? null
          : (v) => onWijzig(put.sleutel, { besluitnummer: v || null })}
      />

      {!uitRegister && (put.kandidaten || []).length > 0 && (
        <Kandidaten
          kop="Putten dichtbij"
          lijst={put.kandidaten}
          onKies={(k) => onWijzig(put.sleutel, {
            soort: 'gekoppeld', put_id: k.put_id, uitRegister: true,
            x_rd: +k.x_rd, y_rd: +k.y_rd, perceel: k.perceel || put.perceel,
            opgegeven_diepte_m: k.opgegeven_diepte_m != null
              ? String(k.opgegeven_diepte_m) : put.opgegeven_diepte_m,
            boorstatus: null,
            registerStatus: k.boorstatus_code || null,
            registerDiepte: k.opgegeven_diepte_m ?? null,
            gemetenDiepte: k.gemeten_diepte_m ?? null,
          })}
        />
      )}

      <div className="pk-voet">
        {/* Een put die al aan de inrichting hangt hálen we eruit; die schrappen
            we niet uit een selectie. Het verschil is niet cosmetisch: het
            eerste is een besluit met een datum, het tweede het corrigeren van
            een vergissing in dit scherm. */}
        {verwijderbaar && put.bestaandeKoppeling ? (
          put.verwijderd ? (
            <button className="btn btn-secondary" onClick={() => onWijzig(put.sleutel, { verwijderd: false })}>
              Toch houden
            </button>
          ) : (
            <button className="pk-weg" onClick={() => onWijzig(put.sleutel, { verwijderd: true })}>
              Uit de inrichting halen
            </button>
          )
        ) : (
          <button className="pk-weg" onClick={() => onVerwijder(put.sleutel)}>
            Verwijderen uit selectie
          </button>
        )}
      </div>
    </>
  );
}

/* Register-putten in de buurt, als pillen naast elkaar: putnummer en afstand.
   Meer hoef je niet te weten om te kiezen, en zo passen ze op één regel. */
function Kandidaten({ kop, lijst, gedaan, onKies }) {
  return (
    <div className="pk-blok">
      <p className="pk-blok-kop">{kop}</p>
      <div className="pk-pillen">
        {lijst.map((k) => (
          <button key={k.put_id}
            className={`pk-pil ${gedaan === k.put_id ? 'is-gedaan' : ''}`}
            disabled={!!gedaan}
            onClick={() => onKies(k)}>
            {gedaan === k.put_id
              ? <><span className="pk-vink" aria-hidden="true">&#10003;</span> Toegevoegd</>
              : <><span className="mono">{fmtId(k.put_id)},</span> {afstandKort(k.afstand_meter)} m</>}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Een aangewezen plek ────────────────────────────────────────────────
   De vraag wordt altijd gesteld, ook als er geen registerput in de buurt
   ligt. Automatisch "te boren" aannemen is een bewering doen die de
   behandelaar niet heeft gedaan.

   Volgorde: eerst wat je aanwees, dan wat je ermee doet, en pas daarna de
   registerputten in de buurt — die zijn de uitzondering, niet de regel. */
function PlekPaneel({ plek, onAanwijzen, onKoppel, onSoort }) {
  const gedaan = plek.gedaan;
  const knop = (soort, label) => (
    <button className={`pk-pil ${gedaan === soort ? 'is-gedaan' : ''}`}
      disabled={!!gedaan} onClick={() => onSoort(soort)}>
      {gedaan === soort
        ? <><span className="pk-vink" aria-hidden="true">&#10003;</span> Toegevoegd</>
        : label}
    </button>
  );

  return (
    <>
      <h4 className="pk-paneel-titel">Locatiegegevens</h4>
      <Regel label="Coördinaten (RD)">
        <span className="mono">{Math.round(plek.x)}, {Math.round(plek.y)}</span>
      </Regel>
      <Regel label="Perceel">
        {plek.bezig ? <span className="pk-laden">Laden…</span> : (plek.perceel || <Leeg />)}
      </Regel>
      {/* Wat de keurkaart hier toestaat. Naslag: bij een vergunning bepaalt de
          beschikking de diepte, niet deze twee lagen. */}
      {plek.diepte11 !== undefined && (
        <>
          <Regel label="Max. diepte (G/K)">
            {meters(plek.diepte11)
              ? <>{meters(plek.diepte11)}<span className="pk-eenheid">m</span></> : <Leeg />}
          </Regel>
          <Regel label="Max. diepte (O)">
            {meters(plek.diepte12)
              ? <>{meters(plek.diepte12)}<span className="pk-eenheid">m</span></> : <Leeg />}
          </Regel>
        </>
      )}
      <GebiedPillen lijst={plek.ligging} onAanwijzen={onAanwijzen} />

      {/* Geweigerd: wél de locatiegegevens, geen knop. Wie een coördinaat
          intypt heeft geen aanwijzer gezien en zou anders naar een scherm
          kijken dat niets zegt. */}
      {plek.geweigerd ? (
        <p className="pk-nee-tekst">
          In een kwetsbaar gebied kan geen put worden opgevoerd.
        </p>
      ) : (
        <div className="pk-blok">
          <p className="pk-blok-kop">Toevoegen als</p>
          <div className="pk-pillen">
            {knop('ongeregistreerd', 'Geboord')}
            {knop('nieuw', 'Te boren')}
          </div>
        </div>
      )}

      {plek.kandidaten.length > 0 && (
        <Kandidaten kop="Putten dichtbij" lijst={plek.kandidaten} gedaan={gedaan} onKies={onKoppel} />
      )}
    </>
  );
}

/* ── Een put uit het register ─────────────────────────────────────────── */
function RegisterPaneel({ rp, gedaan, diepte11, diepte12, liggingOp, onAanwijzen, onKoppel }) {
  return (
    <>
      {/* Grijze stip, net als de marker: deze put hoort nog niet bij de
          selectie. Blauw zou hem alvast als gekozen laten lezen. */}
      <h4 className="pk-paneel-titel">
        <Stip soort="register" groot /><span className="mono">{fmtId(rp.put_id)}</span>
      </h4>
      <Regel label="Coördinaten (RD)">
        <span className="mono">{Math.round(rp.x_rd)}, {Math.round(rp.y_rd)}</span>
      </Regel>
      <Regel label="Perceel">{rp.perceel || <Leeg />}</Regel>
      {diepte11 !== undefined && (
        <>
          <Regel label="Maximale boordiepte">
            {meters(rp.max_diepte_mv)
              ? <>{meters(rp.max_diepte_mv)}<span className="pk-eenheid">m</span></> : <Leeg />}
          </Regel>
          <Regel label="Max. diepte (G/K)">
            {meters(diepte11)
              ? <>{meters(diepte11)}<span className="pk-eenheid">m</span></> : <Leeg />}
          </Regel>
          <Regel label="Max. diepte (O)">
            {meters(diepte12)
              ? <>{meters(diepte12)}<span className="pk-eenheid">m</span></> : <Leeg />}
          </Regel>
        </>
      )}
      <Regel label="Opgegeven diepte">
        {rp.opgegeven_diepte_m != null
          ? <>{meters(rp.opgegeven_diepte_m)}<span className="pk-eenheid">m</span></>
          : <Leeg />}
      </Regel>
      <GebiedPillen lijst={liggingOp(+rp.x_rd, +rp.y_rd)} onAanwijzen={onAanwijzen} />
      {/* Geen onKoppel meegekregen betekent: hier valt niets te koppelen, dit
          kaartje is om te lezen. Dan hoort er ook geen knop onder te staan. */}
      {onKoppel && (
        <div className="pk-blok">
          <div className="pk-pillen">
            <button className={`pk-pil ${gedaan ? 'is-gedaan' : ''}`} disabled={!!gedaan} onClick={onKoppel}>
              {gedaan
                ? <><span className="pk-vink" aria-hidden="true">&#10003;</span> Toegevoegd</>
                : 'Toevoegen'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
